import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import useBreakpoint from '../../../hooks/useBreakpoint/index.js';
import styles from './search.module.scss.js';
import { CircleXIcon } from '../../Icons/index.js';

/* eslint-disable camelcase */
function Input(_ref) {
  let {
    tracking,
    query,
    setQuery,
    searchUrl,
    setSearched,
    pageType,
    trackClientContentAction,
    attachClientImpressionHandler
  } = _ref;
  const ref = useRef(null);
  const [placeholder, setPlaceholder] = useState('search for products, guides, and more!');
  const isMobile = useBreakpoint('(max-width:640px)');

  // track impressions only once, even if re-rendered
  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    attachClientImpressionHandler(element, {}, {
      ...tracking,
      item_name: 'search',
      item_type: 'submission',
      target_content_name: 'search',
      target_content_type: 'submission'
    });
  }, [tracking]);
  const handleKeyDown = event => {
    if (typeof query === 'undefined' || query === '') {
      return;
    }
    if (event.key === 'Enter') {
      trackClientContentAction({}, {
        ...tracking,
        item_name: 'search',
        item_type: 'submission',
        action_value: event.target.value,
        action_type: 'search'
      });
      document.location.href = searchUrl;
    }
  };
  const handleChange = event => {
    setQuery(event.target.value);
    setSearched(true);
  };
  const handleFocus = () => {
    setSearched(true);
  };
  useEffect(() => {
    if (pageType === 'default' || isMobile) {
      setPlaceholder('search for products, guides, and more!');
    } else {
      setPlaceholder('search on BuzzFeed Shopping');
    }
  }, [pageType, isMobile]);
  return /*#__PURE__*/React.createElement("input", {
    ref: ref,
    className: styles.search,
    placeholder: placeholder,
    value: query,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    maxLength: "40"
  });
}
function SearchButton(_ref2) {
  let {
    tracking,
    query,
    searchUrl,
    trackClientContentAction
  } = _ref2;
  const handleSearchButton = () => {
    if (typeof query === 'undefined' || query === '') {
      return;
    }
    trackClientContentAction({}, {
      ...tracking,
      item_name: 'search_submit',
      item_type: 'submission',
      action_type: 'search',
      action_value: query
    });
    document.location.href = searchUrl;
  };
  return /*#__PURE__*/React.createElement("div", {
    role: "button",
    onClick: handleSearchButton,
    onKeyDown: handleSearchButton,
    className: styles.searchButton,
    tabIndex: 0
  }, "Search");
}
function ClearIcon(_ref3) {
  let {
    setQuery,
    setSearched,
    tracking,
    trackClientContentAction
  } = _ref3;
  const handleClear = () => {
    setQuery('');
    setSearched(false);
    trackClientContentAction({}, {
      ...tracking,
      item_name: 'search_clear',
      item_type: 'button',
      action_value: 'clear',
      action_type: 'hide'
    });
  };
  return /*#__PURE__*/React.createElement(CircleXIcon, {
    className: styles.clear,
    role: "button",
    tabIndex: 0,
    title: 'Clear',
    onClick: handleClear,
    onKeyDown: handleClear
  });
}
function CancelButton(_ref4) {
  let {
    setQuery,
    setSearched,
    tracking,
    trackClientContentAction
  } = _ref4;
  const handleCancel = () => {
    setQuery('');
    setSearched(false);
    trackClientContentAction({}, {
      ...tracking,
      item_name: 'search_cancel',
      item_type: 'button',
      action_value: 'cancel',
      action_type: 'hide'
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: styles.cancel,
    role: "button",
    tabIndex: 0,
    onClick: handleCancel,
    onKeyDown: handleCancel
  }, "Cancel");
}
function SearchInput(_ref5) {
  let {
    searchQuery,
    setSearched,
    tracking,
    inputWrapRef,
    pageType,
    trackClientContentAction,
    attachClientImpressionHandler,
    baseUrl,
    baseParamsString
  } = _ref5;
  // set search input to query extracted from url, and allow us to update search query
  const [query, setQuery] = useState(searchQuery);
  // generate a search url that will be used in links and buttons in search input and search menu
  const searchUrlBase = `${baseUrl}/search${baseParamsString ? '?' + baseParamsString : ''}`;
  const searchUrl = useMemo(() => `${searchUrlBase}${baseParamsString ? '&' : '?'}q=${encodeURIComponent(query)}`, [query, searchUrlBase, baseParamsString]);
  // set common tracking info
  const subunitTrackingLayer = {
    ...tracking,
    subunit_type: 'component',
    subunit_name: 'search_input'
  };
  return /*#__PURE__*/React.createElement("div", {
    className: `${styles.searchWrapper} ${pageType !== 'default' ? styles.searchWrapperCategory : ''}`
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.inputWrapper,
    ref: inputWrapRef
  }, /*#__PURE__*/React.createElement(Input, {
    tracking: subunitTrackingLayer,
    query: query,
    setQuery: setQuery,
    searchUrl: searchUrl,
    setSearched: setSearched,
    trackClientContentAction: trackClientContentAction,
    attachClientImpressionHandler: attachClientImpressionHandler,
    pageType: pageType
  }), /*#__PURE__*/React.createElement(SearchButton, {
    tracking: subunitTrackingLayer,
    query: query,
    searchUrl: searchUrl,
    trackClientContentAction: trackClientContentAction
  }), query && /*#__PURE__*/React.createElement(ClearIcon, {
    setQuery: setQuery,
    setSearched: setSearched,
    tracking: subunitTrackingLayer,
    trackClientContentAction: trackClientContentAction
  })), query && /*#__PURE__*/React.createElement(CancelButton, {
    setQuery: setQuery,
    setSearched: setSearched,
    tracking: subunitTrackingLayer,
    trackClientContentAction: trackClientContentAction
  }));
}
SearchInput.propTypes = {
  query: PropTypes.string,
  searchUrl: PropTypes.string,
  tracking: PropTypes.object
};

export { SearchInput, SearchInput as default };
